"use client";

import { Minus, Plus } from "lucide-react";
import * as React from "react";

import { JsonLdScript } from "@/components/structured-data/json-ld-script";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

import { FAQS } from "./faqs-data";
import { Section } from "./shared";

export function Faqs() {
  const [openValue, setOpenValue] = React.useState<string | undefined>(
    FAQS[1]?.question
  );
  const answered = FAQS.filter((faq) => Boolean(faq.answer));
  const faqPageSchema =
    answered.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: answered.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : null;

  return (
    <>
      {faqPageSchema && <JsonLdScript json={faqPageSchema} />}
      <Section>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-10 border border-[#e6e6e6] bg-white p-6 lg:flex-row lg:gap-[113px] lg:p-16">
          <h2 className="m-0 w-full shrink-0 text-[32px] leading-[1.2] font-normal tracking-[-0.04em] text-[#0d0d0d] lg:w-[260px]">
            Frequently asked questions
          </h2>
          <Accordion
            value={openValue ? [openValue] : []}
            onValueChange={(value) => setOpenValue(value[0])}
            className="flex w-full flex-col gap-4 lg:w-[780px]"
          >
            {FAQS.map((faq) => {
              const hasAnswer = Boolean(faq.answer);
              return (
                <AccordionItem
                  key={faq.question}
                  value={faq.question}
                  disabled={!hasAnswer}
                  className="group flex flex-col gap-4 rounded-2xl border border-[#e6e6e6] bg-[#fafaf7] p-6"
                >
                  <AccordionPrimitive.Header className="flex">
                    <AccordionPrimitive.Trigger
                      className={cn(
                        "flex w-full items-center gap-4 text-left outline-none",
                        !hasAnswer && "cursor-default"
                      )}
                    >
                      <span className="flex-1 text-[16px] leading-[1.5] tracking-[-0.02em] text-[#0d0d0d]">
                        {faq.question}
                      </span>
                      {hasAnswer && (
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-[#e6e6e6] bg-white">
                          <Minus
                            className="hidden size-3 group-data-panel-open:block"
                            strokeWidth={1.3}
                            aria-hidden="true"
                          />
                          <Plus
                            className="size-3 group-data-panel-open:hidden"
                            strokeWidth={1.3}
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  {hasAnswer && (
                    <AccordionContent className="pb-0">
                      <p className="m-0 text-[16px] leading-[1.5] tracking-[-0.02em] text-[#0d0d0d]">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  )}
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </Section>
    </>
  );
}
